import '../chunk-PZ5AY32C.js';

// src/budgets/index.ts
var LOAD_PROFILES = ["lean", "standard", "deep"];
var LOAD_PROFILE_KEY = "load_profile";
var LOAD_BUDGETS = {
  // -- transactions ---------------------------------------------------------
  /** The transactions explorer feed — the heaviest table in the product. */
  "transactions.feed": { rows: 50, ceiling: 500, enrichment: 50 },
  /** The graph built around one transaction's participants. */
  "transactions.graph": { rows: 100, ceiling: 100, enrichment: 100 },
  /** Rows folded into scenes for the table's expandable detail. */
  "transactions.scenes": { rows: 150, ceiling: 150, enrichment: 150 },
  /** Participants resolved eagerly on a transaction, ahead of the full read. */
  "transactions.participants": { rows: 20, ceiling: 20, enrichment: 20 },
  // -- addresses ------------------------------------------------------------
  /** The transaction table on one address. */
  "addresses.transactions": { rows: 50, ceiling: 500, enrichment: 50 },
  /** An address's NFT holdings. */
  "addresses.nfts": { rows: 50, ceiling: 500, enrichment: 0 },
  /** An address's current token balances. */
  "addresses.balances": { rows: 100, ceiling: 500, enrichment: 0 },
  /** Live token holder pages; explicit next-page reads, never an automatic walk. */
  "tokens.holders": { rows: 50, ceiling: 500, enrichment: 50 },
  /**
   * Distinct token contracts a surface will look up decimals + symbol for.
   *
   * 100 is `POST /tokens/batch`'s own cap, so the standard profile is exactly
   * one round trip however big the graph is. A 500-node tracer run names
   * dozens of contracts, most of them one-edge spam airdrops, and the refs are
   * ordered by how many edges each carries — so where this does bite, what it
   * drops is the tail rather than the token the investigation is about.
   */
  "tokens.metadata": { rows: 100, ceiling: 100, enrichment: 100 },
  // -- contracts ------------------------------------------------------------
  /**
   * The addresses a contract's own fields point at — `token0`, `factory`,
   * `owner`, every member of a `getOwners()`.
   *
   * `rows`/`ceiling`: how many distinct addresses the page will name at all.
   * They arrive from the getters rather than from a page of results, so there
   * is no paging here and the two are the same number; what they bound is one
   * batched catalog read, the contract itself first so it can never be the ref
   * that gets dropped.
   *
   * `enrichment` is the expensive half and is deliberately smaller: an address
   * the catalog could not name is probed for `name()` ON CHAIN, which is a
   * round trip EACH. Most tokens land there — WETH's own address page says "No
   * overlays resolve for this address" — so this is the dial that decides
   * whether a pool's two tokens read as "Wrapped Ether" or as hex.
   */
  "contracts.fields": { rows: 16, ceiling: 16, enrichment: 8 },
  // -- catalog --------------------------------------------------------------
  /** Automatic address catalog second pass, per mounted surface. */
  "catalog.enrichment": { rows: 100, ceiling: 100, enrichment: 100 },
  /** Catalog rows, and the addresses/entities attached to one. */
  "catalog.list": { rows: 50, ceiling: 500, enrichment: 50 },
  // -- investigations and detections ---------------------------------------
  /** The case list. */
  "investigations.list": { rows: 50, ceiling: 500, enrichment: 0 },
  /** Pipeline runs belonging to one case. */
  "investigations.runs": { rows: 100, ceiling: 500, enrichment: 0 },
  /** The watch feed — what a watch has seen. */
  "watches.feed": { rows: 100, ceiling: 500, enrichment: 100 },
  /** The discovery review queue. */
  "proposals.list": { rows: 50, ceiling: 500, enrichment: 50 },
  // -- landing / live -------------------------------------------------------
  /** The live on-chain feed. */
  "onchain.feed": { rows: 25, ceiling: 500, enrichment: 25 },
  /** The short preview above it. */
  "onchain.preview": { rows: 15, ceiling: 15, enrichment: 15 },
  /** The latest-blocks head. */
  "onchain.blocks": { rows: 40, ceiling: 40, enrichment: 0 },
  // -- automate and records -------------------------------------------------
  /** Pipeline runs. */
  "runs.list": { rows: 50, ceiling: 500, enrichment: 0 },
  /** Pools under an entity or address. */
  "pools.list": { rows: 200, ceiling: 500, enrichment: 0 },
  /** The export ledger. */
  "exports.list": { rows: 100, ceiling: 500, enrichment: 0 },
  /**
   * The workspace's raw call trail (Activity → Audit).
   *
   * `rows` is deliberately modest against a table that grows by one row per
   * INVOCATION — reads included, about ten per page load, 408,000 in two months
   * on one sandbox workspace. There is no ceiling walk and no enrichment: every
   * page is a click, because a surface that auto-walks this table walks
   * forever.
   */
  "activity.audit": { rows: 50, ceiling: 500, enrichment: 0 }
};
var PROFILE_SCALE = {
  lean: 0.5,
  standard: 1,
  deep: 2
};
var MIN_ROWS = 5;
function scaled(value, factor) {
  if (value <= 0) return 0;
  return Math.max(MIN_ROWS, Math.round(value * factor));
}
function budgetFor(name, profile = "standard") {
  const base = LOAD_BUDGETS[name];
  const factor = PROFILE_SCALE[profile] ?? PROFILE_SCALE.standard;
  if (factor === 1) return { ...base };
  return {
    ...base,
    rows: scaled(base.rows, factor),
    ceiling: scaled(base.ceiling, factor),
    enrichment: scaled(base.enrichment, factor)
  };
}
function loadProfileFrom(metadata) {
  const raw = metadata?.[LOAD_PROFILE_KEY];
  if (typeof raw !== "string") return null;
  const value = raw.trim().toLowerCase();
  return LOAD_PROFILES.includes(value) ? value : null;
}
function resolveLoadProfile(planMetadata, subscriptionMetadata = null) {
  return loadProfileFrom(subscriptionMetadata) ?? loadProfileFrom(planMetadata) ?? "standard";
}

export { LOAD_BUDGETS, LOAD_PROFILES, LOAD_PROFILE_KEY, PROFILE_SCALE, budgetFor, loadProfileFrom, resolveLoadProfile };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map